import {
	keepPreviousData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import type { InferRequestType } from "hono/client";
import client from "../../utils/api-client";

// types

type GetMenuItemsRoute = (typeof client.menu.items)["$get"];
type GetMenuItemsInput = InferRequestType<GetMenuItemsRoute>["query"];

type GetMenuItemByDisplayIdRoute =
	(typeof client.menu.items)["display-id"]["$get"];
type GetMenuItemByDisplayIdInput =
	InferRequestType<GetMenuItemByDisplayIdRoute>["query"];

type MenuItemRouteWithId = (typeof client.menu.items)[":id"];

type GetMenuItemByIdRoute = MenuItemRouteWithId["$get"];
type GetMenuItemByIdInput = InferRequestType<GetMenuItemByIdRoute>["param"];

type CreateMenuItemRoute = (typeof client.menu.items)["$post"];
type CreateMenuItemInput = InferRequestType<CreateMenuItemRoute>["json"];

type UpdateMenuItemRoute = MenuItemRouteWithId["$put"];
type UpdateMenuItemRouteInput = InferRequestType<UpdateMenuItemRoute>;

type GetCategoryImagesRoute =
	(typeof client.menu.categories)[":category"]["images"]["$get"];
type GetCategoryImagesInput = InferRequestType<GetCategoryImagesRoute>["param"];

type GetCategorySizesRoute =
	(typeof client.menu.categories)[":id"]["sizes"]["$get"];
type GetCategorySizesInput = InferRequestType<GetCategorySizesRoute>["param"];

type CreateSizeRoute = (typeof client.menu.sizes)["$post"];
type CreateSizeInput = InferRequestType<CreateSizeRoute>["json"];

type CreateAllergyRoute = (typeof client.menu.allergies)["$post"];
type CreateAllergyInput = InferRequestType<CreateAllergyRoute>["json"];

type GetExtrasRoute = (typeof client.menu.extras)["$get"];
type GetExtrasInput = InferRequestType<GetExtrasRoute>["query"];

type CreateExtraRoute = (typeof client.menu.extras)["$post"];
type CreateExtraInput = InferRequestType<CreateExtraRoute>["json"];

type ExtraRouteWithId = (typeof client.menu.extras)[":id"];

type UpdateExtraRoute = ExtraRouteWithId["$put"];
type UpdateExtraRouteInput = InferRequestType<UpdateExtraRoute>;

type DeleteExtraRoute = ExtraRouteWithId["$delete"];
type DeleteExtraInput = InferRequestType<DeleteExtraRoute>["param"];

type SearchMenuItemsRoute = (typeof client.menu.search)["$get"];
type SearchMenuItemsInput = InferRequestType<SearchMenuItemsRoute>["query"];

export type MenuApiRequestTypes = {
	getItems: GetMenuItemsInput;
	getItemsInfinite: GetMenuItemsInput;
	getItemById: GetMenuItemByIdInput;
	getItemByDisplayId: GetMenuItemByDisplayIdInput;
	getCategoryImages: GetCategoryImagesInput;
	getCategorySizes: GetCategorySizesInput;
	searchItems: SearchMenuItemsInput;
	getExtras: GetExtrasInput;
	createItem: CreateMenuItemInput;
	updateItem: UpdateMenuItemRouteInput["param"] & {
		data: UpdateMenuItemRouteInput["json"];
	};
	createSize: CreateSizeInput;
	createAllergy: CreateAllergyInput;
	createExtra: CreateExtraInput;
	updateExtra: UpdateExtraRouteInput["param"] & {
		data: UpdateExtraRouteInput["json"];
	};
	deleteExtra: DeleteExtraInput;
};

export type MenuApiResponseTypes = {
	getItems: Awaited<ReturnType<typeof useGetMenuItemsQuery>>["data"];
	getItemsInfinite: Awaited<
		ReturnType<typeof useGetMenuItemsInfiniteQuery>
	>["data"];
	getItemById: Awaited<ReturnType<typeof useGetMenuItemByIdQuery>>["data"];
	getItemByDisplayId: Awaited<
		ReturnType<typeof useGetMenuItemByDisplayIdQuery>
	>["data"];
	getCategories: Awaited<ReturnType<typeof useGetCategoriesQuery>>["data"];
	getCategoryImages: Awaited<
		ReturnType<typeof useGetCategoryImagesQuery>
	>["data"];
	getCategorySizes: Awaited<
		ReturnType<typeof useGetCategorySizesQuery>
	>["data"];
	getSizes: Awaited<ReturnType<typeof useGetSizesQuery>>["data"];
	getAllergies: Awaited<ReturnType<typeof useGetAllergiesQuery>>["data"];
	getExtras: Awaited<ReturnType<typeof useGetExtrasQuery>>["data"];
	searchItems: Awaited<ReturnType<typeof useSearchMenuItemsQuery>>["data"];
	createItem: Awaited<ReturnType<typeof useCreateMenuItemMutation>>["data"];
	updateItem: Awaited<ReturnType<typeof useUpdateMenuItemMutation>>["data"];
	createSize: Awaited<ReturnType<typeof useCreateSizeMutation>>["data"];
	createAllergy: Awaited<ReturnType<typeof useCreateAllergyMutation>>["data"];
	createExtra: Awaited<ReturnType<typeof useCreateExtraMutation>>["data"];
	updateExtra: Awaited<ReturnType<typeof useUpdateExtraMutation>>["data"];
	deleteExtra: Awaited<ReturnType<typeof useDeleteExtraMutation>>["data"];
};

const staticStaleTime = {
	staleTime: 1000 * 60 * 10, // 10 minutes
	gcTime: 1000 * 60 * 60, // Keep in RAM for 1 hour
};

// queries
export function useGetMenuItemsQuery(query: GetMenuItemsInput) {
	return useQuery({
		...staticStaleTime,
		queryKey: ["menu", "items", query],
		queryFn: async () => {
			const result = await client.menu.items.$get({ query });
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		enabled: true,
		placeholderData: keepPreviousData,
	});
}

export function useGetMenuItemsInfiniteQuery(query: GetMenuItemsInput) {
	const limit = Number(query.limit ?? 10);

	return useInfiniteQuery({
		...staticStaleTime,
		queryKey: ["menu", "items", "infinite", query],
		queryFn: async ({ pageParam }) => {
			const offset = Number(pageParam);
			const result = await client.menu.items.$get({
				query: {
					category: query.category,
					limit,
					offset,
				},
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		getNextPageParam: (lastPage) => {
			if (!lastPage.pagination?.hasNextPage) return undefined;
			return (
				Number(lastPage.pagination.offset) + Number(lastPage.pagination.limit)
			);
		},
		initialPageParam: Number(query.offset ?? 0),
		enabled: true,
	});
}

export function useGetMenuItemByIdQuery(id: number) {
	return useQuery({
		queryKey: ["menu", "items", id],
		queryFn: async () => {
			const result = await client.menu.items[":id"].$get({
				param: { id: id.toString() },
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		enabled: !!id,
	});
}

export function useGetMenuItemByDisplayIdQuery(displayId: string) {
	return useQuery({
		queryKey: ["menu", "items", "display-id", displayId],
		queryFn: async () => {
			const result = await client.menu.items["display-id"].$get({
				query: { displayId },
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		enabled: !!displayId,
	});
}

export function useGetCategoriesQuery() {
	return useQuery({
		...staticStaleTime,
		queryKey: ["menu", "categories"],
		queryFn: async () => {
			const result = await client.menu.categories.$get();
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data.categories;
		},
		enabled: true,
	});
}

export function useGetCategoryImagesQuery(param: GetCategoryImagesInput) {
	return useQuery({
		queryKey: ["menu", "categories", param.category, "images"],
		queryFn: async () => {
			const result = await client.menu.categories[":category"].images.$get({
				param,
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data.images;
		},
		...staticStaleTime,
		enabled: !!param.category,
	});
}

export function useGetCategorySizesQuery(param: GetCategorySizesInput) {
	return useQuery({
		queryKey: ["menu", "categories", param.id, "sizes"],
		queryFn: async () => {
			const result = await client.menu.categories[":id"].sizes.$get({ param });
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data.sizes;
		},
		...staticStaleTime,
		enabled: Number(param.id) > 0,
	});
}

export function useGetSizesQuery() {
	return useQuery({
		...staticStaleTime,
		queryKey: ["menu", "sizes"],
		queryFn: async () => {
			const result = await client.menu.sizes.$get();
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data.sizes;
		},
		enabled: true,
	});
}

export function useGetAllergiesQuery() {
	return useQuery({
		...staticStaleTime,
		queryKey: ["menu", "allergies"],
		queryFn: async () => {
			const result = await client.menu.allergies.$get();
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data.allergies;
		},
		enabled: true,
	});
}

export function useGetExtrasQuery(query: GetExtrasInput) {
	return useQuery({
		...staticStaleTime,
		queryKey: ["menu", "extras", query],
		queryFn: async () => {
			const result = await client.menu.extras.$get({ query });
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		enabled: true,
	});
}

export function useSearchMenuItemsQuery(query: SearchMenuItemsInput) {
	const searchTerm = Array.isArray(query.q) ? query.q[0] : query.q;

	return useQuery({
		queryKey: ["menu", "search", query],
		queryFn: async () => {
			const result = await client.menu.search.$get({ query });
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		enabled: typeof searchTerm === "string" && searchTerm.trim().length >= 2,
	});
}

// mutations
export function useCreateMenuItemMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (json: CreateMenuItemInput) => {
			const result = await client.menu.items.$post({ json });
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["menu", "items"] });
			queryClient.invalidateQueries({ queryKey: ["menu", "categories"] });
		},
	});
}

export function useUpdateMenuItemMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			id: number;
			data: UpdateMenuItemRouteInput["json"];
		}) => {
			const result = await client.menu.items[":id"].$put({
				param: { id: input.id.toString() },
				json: input.data,
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		onSuccess: (updatedItem, variables) => {
			// Immediate cache patch to avoid a refetch
			queryClient.setQueryData(["menu", "items", variables.id], updatedItem);

			const displayId = updatedItem?.item?.displayId;
			queryClient.setQueryData(
				["menu", "items", "display-id", displayId],
				updatedItem,
			);

			queryClient.invalidateQueries({ queryKey: ["menu", "items"] });
			queryClient.invalidateQueries({
				queryKey: ["menu", "items", variables.id],
			});
		},
	});
}

export function useCreateSizeMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (json: CreateSizeInput) => {
			const result = await client.menu.sizes.$post({ json });
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["menu", "sizes"] });
			queryClient.invalidateQueries({
				queryKey: ["menu", "items", variables.menuItemId],
			});
		},
	});
}

export function useCreateAllergyMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (json: CreateAllergyInput) => {
			const result = await client.menu.allergies.$post({ json });
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["menu", "allergies"] });
			queryClient.invalidateQueries({
				queryKey: ["menu", "items", variables.menuItemId],
			});
		},
	});
}

export function useCreateExtraMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (json: CreateExtraInput) => {
			const result = await client.menu.extras.$post({ json });
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["menu", "extras"] });
		},
	});
}

export function useUpdateExtraMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			id: number;
			data: UpdateExtraRouteInput["json"];
		}) => {
			const result = await client.menu.extras[":id"].$put({
				param: { id: input.id.toString() },
				json: input.data,
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["menu", "extras"] });
		},
	});
}

export function useDeleteExtraMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (id: number) => {
			const result = await client.menu.extras[":id"].$delete({
				param: { id: id.toString() },
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["menu", "extras"] });
		},
	});
}
